import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Box, CircularProgress } from '@mui/material';
import { getCookie } from '../utils/cookies';

/**
 * ProtectedRoute - Guards routes that require authentication.
 * Checks both Redux auth state and cookie token to prevent
 * stale data from rendering protected content.
 *
 * Ensures authenticated branding is loaded before rendering
 * the authenticated layout to completely prevent color/branding flicker.
 */
const ProtectedRoute = ({ children }) => {
    const isAuth = useSelector((state) => state.auth.isAuthenticated);
    const hasToken = !!getCookie('adminToken');
    const isCustomLoaded = useSelector((state) => state.branding.isCustomLoaded);
    const location = useLocation();

    if (!isAuth || !hasToken) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }

    const authData = JSON.parse(getCookie('adminAuth') || localStorage.getItem('adminAuth') || '{}');
    const roleName = authData.role?.name || authData.role || '';
    const isSuperAdmin = String(roleName).toUpperCase().replace(/[_\s-]+/g, "") === "SUPERADMIN";
    const tenantId = authData.tenantId;

    // If tenant context (tenantId) is not present:
    // - Dashboard access is blocked: Super Admin is redirected to /users (User List), regular users to /select-tenant.
    // - Regular users are forced to select-tenant for any other layout page.
    if (!tenantId || tenantId === 'null') {
        if (location.pathname === '/dashboard' || location.pathname === '/') {
            if (isSuperAdmin) {
                return <Navigate to="/users" replace />;
            } else {
                return <Navigate to="/select-tenant" replace />;
            }
        }
        if (!isSuperAdmin && location.pathname !== '/select-tenant') {
            return <Navigate to="/select-tenant" replace />;
        }
    }

    // Guard layout until tenant branding is loaded to prevent old branding bleed or flicker
    if (tenantId && !isCustomLoaded) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '100vh',
                    width: '100%',
                    bgcolor: '#ffffff',
                }}
            >
                <CircularProgress size={36} thickness={4} sx={{ color: '#94a3b8' }} />
            </Box>
        );
    }

    return children;
};

export default ProtectedRoute;
